import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { useAccess } from '../../contexts/AccessContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ROLE_LABELS, SERVER_PERMISSIONS } from '../../config/permissions';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import ServerIcon from '../ui/ServerIcon';
import SiteCensusSection from '../personnages/SiteCensusSection';
import './AdminPage.css';

// Droits proposés à l'attribution (hors pages masquées)
const GRANTABLE = SERVER_PERMISSIONS.filter(p => !p.hidden);
// Nombre de droits visibles (un droit masqué, ex. Événements, peut rester stocké)
const countGrantable = (perms = []) => GRANTABLE.filter(p => perms.includes(p.key)).length;
const ROLE_ORDER = { owner: 0, siteAdmin: 1, serverAdmin: 2, member: 3 };
const FILTERS = [
    { id: 'admins', labelKey: 'adminSite.filterAdmins', label: 'Administrateurs' },
    { id: 'all', labelKey: 'adminSite.filterAll', label: 'Tout le monde' }
];
// Vues de la page : gestion des administrateurs, recensement des personnages
const VIEWS = [
    { id: 'admins', labelKey: 'adminSite.admins', label: 'Administrateurs', icon: 'shield' },
    { id: 'personnages', labelKey: 'navigation.personnages', label: 'Personnages', icon: 'users' }
];

const avatarUrl = (u) => (u.avatar ? `https://cdn.discordapp.com/avatars/${u.discordId}/${u.avatar}.png?size=64` : null);

const RoleBadge = ({ role }) => {
    const { t } = useLanguage();
    return <span className={`c2-chip admin-role admin-role--${role}`}>{t(`adminSite.roles.${role}`, ROLE_LABELS[role])}</span>;
};

/* ── Modale : droits d'un Administrateur Serveur, serveur par serveur ── */
const ServerAccessModal = ({ user, servers, onClose, onSaved }) => {
    const { success, error: showError } = useNotification();
    const { t } = useLanguage();
    const initial = useMemo(
        () => Object.fromEntries((user.serverAccess || []).map(a => [a.serverId, a.permissions])),
        [user]
    );
    const [draft, setDraft] = useState(initial);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);

    const permsOf = (serverId) => draft[serverId] || [];
    const setPerms = (serverId, perms) => setDraft(prev => ({ ...prev, [serverId]: perms }));
    const togglePerm = (serverId, key) => {
        const current = permsOf(serverId);
        setPerms(serverId, current.includes(key) ? current.filter(k => k !== key) : [...current, key]);
    };

    const sameSet = (a = [], b = []) => a.length === b.length && a.every(x => b.includes(x));
    const changedIds = servers.map(s => s.id).filter(id => !sameSet(permsOf(id), initial[id]));

    // Serveurs déjà accordés en premier, puis par nom
    const visibleServers = servers
        .filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => ((initial[b.id]?.length ? 1 : 0) - (initial[a.id]?.length ? 1 : 0)) || a.name.localeCompare(b.name));

    const save = async () => {
        setSaving(true);
        try {
            let last = null;
            for (const serverId of changedIds) {
                const res = await api.put(`/admin/users/${user.discordId}/servers/${serverId}`, { permissions: permsOf(serverId) });
                last = res.data;
            }
            success(t('adminSite.rightsSaved', 'Droits de {name} enregistrés', { name: user.username }));
            onSaved(user.discordId, last);
        } catch (err) {
            showError(err?.response?.data?.error || t('adminSite.rightsSaveError', 'Erreur lors de l\'enregistrement des droits'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="c2-scope c2-modal-backdrop" role="presentation">
            <div className="c2-card c2-modal admin-access-modal" role="dialog" aria-modal="true" aria-labelledby="access-modal-title">
                <div className="c2-modal__head">
                    <h2 id="access-modal-title">{t('adminSite.rightsOf', 'Droits de {name}', { name: user.username })}</h2>
                    <button type="button" className="c2-icon-btn" onClick={onClose} disabled={saving} aria-label={t('common.close', 'Fermer')}>
                        <Icon name="close" size={20} />
                    </button>
                </div>
                <p className="c2-muted admin-access-modal__intro">
                    {t('adminSite.rightsIntro', 'Coche les pages accessibles sur chaque serveur. Un serveur sans aucune page n\'apparaît pas dans son espace admin.')}
                </p>

                <input
                    type="search"
                    className="c2-input c2-input--sm"
                    placeholder={t('adminSite.searchServer', 'Rechercher un serveur...')}
                    aria-label={t('adminSite.searchServerLabel', 'Rechercher un serveur')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />

                <ul className="admin-servers">
                    {visibleServers.map(server => {
                        const perms = permsOf(server.id);
                        const granted = countGrantable(perms);
                        const allOn = granted === GRANTABLE.length;
                        return (
                            <li key={server.id} className={`c2-tile admin-server${granted ? ' is-granted' : ''}`}>
                                <div className="admin-server__head">
                                    <ServerIcon guild={server} size={32} />
                                    <span className="admin-server__name">{server.name}</span>
                                    <span className="c2-muted admin-server__count">{granted}/{GRANTABLE.length}</span>
                                    <button
                                        type="button"
                                        className="c2-btn c2-btn--ghost c2-btn--sm"
                                        onClick={() => setPerms(server.id, allOn ? [] : GRANTABLE.map(p => p.key))}
                                    >
                                        {allOn ? t('adminSite.none', 'Aucun') : t('adminSite.all', 'Tout')}
                                    </button>
                                </div>
                                <div className="admin-perms">
                                    {GRANTABLE.map(p => {
                                        const on = perms.includes(p.key);
                                        return (
                                            <button
                                                key={p.key}
                                                type="button"
                                                className={`admin-perm${on ? ' is-on' : ''}`}
                                                aria-pressed={on}
                                                onClick={() => togglePerm(server.id, p.key)}
                                            >
                                                <Icon name={on ? 'check' : p.icon} size={14} />
                                                {t(`adminSite.perms.${p.key}`, p.label)}
                                            </button>
                                        );
                                    })}
                                </div>
                            </li>
                        );
                    })}
                    {visibleServers.length === 0 && <li className="c2-muted">{t('adminSite.noServerMatch', 'Aucun serveur ne correspond.')}</li>}
                </ul>

                <div className="c2-modal__actions">
                    <button type="button" className="c2-btn c2-btn--ghost" onClick={onClose} disabled={saving}>{t('common.cancel', 'Annuler')}</button>
                    <button type="button" className="c2-btn c2-btn--primary" onClick={save} disabled={saving || changedIds.length === 0}>
                        <Icon name="save" size={18} />
                        {saving ? t('adminSite.saving', 'Enregistrement...') : t('adminSite.save', 'Enregistrer')}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ── Page ── */
const AdminPage = () => {
    const { isOwner, refresh: refreshMyAccess } = useAccess();
    const { success, error: showError } = useNotification();
    const { t } = useLanguage();

    const [users, setUsers] = useState([]);
    const [servers, setServers] = useState([]);
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('admins');
    const [editing, setEditing] = useState(null);       // utilisateur dont on modifie les droits serveur
    const [togglingId, setTogglingId] = useState(null);
    const [view, setView] = useState('admins');

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([api.get('/admin/users'), api.get('/admin/overview')])
            .then(([usersRes, overviewRes]) => {
                setUsers(usersRes.data.users || []);
                setServers(usersRes.data.servers || []);
                setOverview(overviewRes.data);
            })
            .catch(() => showError(t('adminSite.loadError', 'Impossible de charger les administrateurs')))
            .finally(() => setLoading(false));
    }, [showError, t]);

    useEffect(() => { load(); }, [load]);

    const serverName = useCallback(
        (id) => servers.find(s => s.id === id)?.name || id,
        [servers]
    );

    const counts = useMemo(() => users.reduce((acc, u) => ({ ...acc, [u.role]: (acc[u.role] || 0) + 1 }), {}), [users]);

    const visibleUsers = users
        .filter(u => filter === 'all' || u.role !== 'member')
        .filter(u => u.username.toLowerCase().includes(search.toLowerCase()) || u.discordId.includes(search))
        .sort((a, b) => (ROLE_ORDER[a.role] - ROLE_ORDER[b.role]) || a.username.localeCompare(b.username));

    const toggleSiteAdmin = async (user) => {
        const makeAdmin = user.role !== 'siteAdmin';
        const message = makeAdmin
            ? t('adminSite.confirmPromote', 'Nommer {name} Administrateur Site ?\n\nCette personne aura tous les droits sur tous les serveurs et pourra gérer les Administrateurs Serveur.', { name: user.username })
            : t('adminSite.confirmDemote', 'Retirer le rôle Administrateur Site à {name} ?', { name: user.username });
        if (!window.confirm(message)) return;

        setTogglingId(user.discordId);
        try {
            const res = await api.patch(`/admin/users/${user.discordId}/site-admin`, { siteAdmin: makeAdmin });
            setUsers(prev => prev.map(u => (u.discordId === user.discordId ? { ...u, role: res.data.role } : u)));
            success(makeAdmin
                ? t('adminSite.promoted', '{name} est maintenant Administrateur Site', { name: user.username })
                : t('adminSite.demoted', '{name} n\'est plus Administrateur Site', { name: user.username }));
        } catch (err) {
            showError(err?.response?.data?.error || t('adminSite.roleError', 'Erreur lors du changement de rôle'));
        } finally {
            setTogglingId(null);
        }
    };

    const onAccessSaved = (discordId, data) => {
        if (data) {
            setUsers(prev => prev.map(u => (u.discordId === discordId ? { ...u, role: data.role, serverAccess: data.serverAccess } : u)));
        }
        setEditing(null);
        refreshMyAccess();
    };

    return (
        <div className="c2-bg admin-site">
            <div className="c2-scope c2-admin-page admin-site__inner">
                <Link to="/welcome" className="c2-btn c2-btn--ghost c2-btn--sm admin-site__back">
                    <Icon name="arrowLeft" size={16} />
                    {t('adminHome.backToServers', 'Mes serveurs')}
                </Link>

                <PageHeader
                    icon="settings"
                    title={t('adminSite.title', 'Administration du site')}
                    subtitle={isOwner ? t('adminSite.subtitleOwner', 'Propriétaire · gestion des administrateurs') : t('adminSite.subtitleAdmin', 'Administrateur Site · gestion des Administrateurs Serveur')}
                    actions={
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={load} disabled={loading}>
                            <Icon name="refresh" size={18} />
                            {t('common.refresh', 'Actualiser')}
                        </button>
                    }
                />

                {overview && (
                    <div className="c2-kpis">
                        <div className="c2-kpi">
                            <span className="c2-kpi__value">{servers.length}</span>
                            <span className="c2-kpi__label">{t('adminSite.serversWithBot', 'Serveurs avec le bot')}</span>
                        </div>
                        <div className="c2-kpi">
                            <span className="c2-kpi__value">{overview.userCount}</span>
                            <span className="c2-kpi__label">{t('adminSite.usersLogged', 'Utilisateurs connectés')}</span>
                        </div>
                        <div className="c2-kpi">
                            <span className="c2-kpi__value">{(counts.owner || 0) + (counts.siteAdmin || 0)}</span>
                            <span className="c2-kpi__label">{t('adminSite.siteAdmins', 'Admins Site')}</span>
                        </div>
                        <div className="c2-kpi">
                            <span className="c2-kpi__value">{counts.serverAdmin || 0}</span>
                            <span className="c2-kpi__label">{t('adminSite.serverAdmins', 'Admins Serveur')}</span>
                        </div>
                    </div>
                )}

                <div className="admin-filters admin-site__views" role="tablist">
                    {VIEWS.map(v => (
                        <button
                            key={v.id}
                            type="button"
                            role="tab"
                            aria-selected={view === v.id}
                            className={`admin-filter${view === v.id ? ' is-active' : ''}`}
                            onClick={() => setView(v.id)}
                        >
                            <Icon name={v.icon} size={16} />
                            {t(v.labelKey, v.label)}
                        </button>
                    ))}
                </div>

                {view === 'personnages' ? <SiteCensusSection /> : (
                <section className="c2-card c2-section">
                    <div className="c2-section__head">
                        <h2 className="c2-section__title"><Icon name="shield" size={20} />{t('adminSite.admins', 'Administrateurs')}</h2>
                        <div className="admin-site__tools">
                            <div className="admin-filters" role="tablist" aria-label={t('common.filter', 'Filtrer')}>
                                {FILTERS.map(f => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={filter === f.id}
                                        className={`admin-filter${filter === f.id ? ' is-active' : ''}`}
                                        onClick={() => setFilter(f.id)}
                                    >
                                        {t(f.labelKey, f.label)}
                                    </button>
                                ))}
                            </div>
                            <input
                                type="search"
                                className="c2-input c2-input--sm admin-site__search"
                                placeholder={t('adminSite.searchPlaceholder', 'Pseudo ou ID Discord...')}
                                aria-label={t('adminSite.searchLabel', 'Rechercher une personne')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                    <p className="c2-section__desc">
                        {t('adminSite.howTo', 'Pour donner des droits à quelqu\'un, il doit s\'être connecté au site au moins une fois : cherche-le dans « Tout le monde ».')}
                        {isOwner && ` ${t('adminSite.ownerOnly', 'Seul le propriétaire peut nommer des Administrateurs Site.')}`}
                    </p>

                    {loading ? (
                        <div className="c2-loading">
                            <div className="c2-spinner" />
                            <p>{t('common.loading', 'Chargement...')}</p>
                        </div>
                    ) : visibleUsers.length === 0 ? (
                        <div className="c2-empty">
                            <Icon name="user" size={36} />
                            <p>{search ? t('adminSite.noMatch', 'Personne ne correspond à la recherche') : t('adminSite.noAdmin', 'Aucun administrateur pour le moment')}</p>
                        </div>
                    ) : (
                        <ul className="admin-users">
                            {visibleUsers.map(u => {
                                const fullAccess = u.role === 'owner' || u.role === 'siteAdmin';
                                return (
                                    <li key={u.discordId} className="c2-tile admin-user">
                                        <div className="admin-user__id">
                                            {avatarUrl(u) ? (
                                                <img className="admin-user__avatar" src={avatarUrl(u)} alt="" />
                                            ) : (
                                                <span className="admin-user__avatar admin-user__avatar--empty"><Icon name="user" size={18} /></span>
                                            )}
                                            <div className="admin-user__text">
                                                <strong>{u.username}</strong>
                                                <span className="c2-muted admin-user__discord">{u.discordId}</span>
                                            </div>
                                            <RoleBadge role={u.role} />
                                        </div>

                                        <div className="admin-user__access">
                                            {fullAccess ? (
                                                <span className="c2-muted">{t('adminSite.fullAccess', 'Tous les droits sur tous les serveurs')}</span>
                                            ) : u.serverAccess.length === 0 ? (
                                                <span className="c2-muted">{t('adminSite.noAccess', 'Aucun accès admin')}</span>
                                            ) : (
                                                u.serverAccess.map(a => (
                                                    <span key={a.serverId} className="c2-chip" title={a.permissions.map(k => t(`adminSite.perms.${k}`, SERVER_PERMISSIONS.find(p => p.key === k)?.label || k)).join(', ')}>
                                                        {serverName(a.serverId)} · {countGrantable(a.permissions) === GRANTABLE.length
                                                            ? t('adminSite.allRights', 'tout')
                                                            : countGrantable(a.permissions) > 1
                                                                ? t('adminSite.rightsN', '{n} droits', { n: countGrantable(a.permissions) })
                                                                : t('adminSite.rightN', '{n} droit', { n: countGrantable(a.permissions) })}
                                                    </span>
                                                ))
                                            )}
                                        </div>

                                        <div className="admin-user__actions">
                                            {!fullAccess && (
                                                <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => setEditing(u)}>
                                                    <Icon name="pencil" size={16} />
                                                    {t('adminSite.serverRights', 'Droits serveur')}
                                                </button>
                                            )}
                                            {isOwner && u.role !== 'owner' && (
                                                <label className="admin-user__switch">
                                                    <span className="c2-muted">{t('adminSite.siteAdmin', 'Admin Site')}</span>
                                                    <button
                                                        type="button"
                                                        role="switch"
                                                        aria-checked={u.role === 'siteAdmin'}
                                                        aria-label={t('adminSite.siteAdminFor', 'Administrateur Site : {name}', { name: u.username })}
                                                        className={`c2-switch${u.role === 'siteAdmin' ? ' is-on' : ''}`}
                                                        onClick={() => toggleSiteAdmin(u)}
                                                        disabled={togglingId === u.discordId}
                                                    >
                                                        <span className="c2-switch__knob" />
                                                    </button>
                                                </label>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
                )}
            </div>

            {editing && (
                <ServerAccessModal
                    user={editing}
                    servers={servers}
                    onClose={() => setEditing(null)}
                    onSaved={onAccessSaved}
                />
            )}
        </div>
    );
};

export default AdminPage;
